import * as pdfjsLib from 'pdfjs-dist';
// Import the worker directly from node_modules for Vite compatibility
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Set up PDF.js worker - use local bundled worker for reliability
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export const base64ToBlob = (base64: string, mimeType: string): Blob => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType }); // Note: This creates a browser Blob.
};

export const readTextFile = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      resolve(event.target?.result as string);
    };
    reader.onerror = (error) => reject(error);
    reader.readAsText(file);
  });
};

/**
 * Extract text content from a PDF file
 * @param file The PDF file to extract text from
 * @returns Promise with the extracted text content
 * @throws Error if PDF cannot be parsed or contains no text (image-based PDF)
 */
export const extractTextFromPDF = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const typedArray = new Uint8Array(event.target?.result as ArrayBuffer);

        // Load the PDF document
        const loadingTask = pdfjsLib.getDocument({ data: typedArray });
        const pdf = await loadingTask.promise;

        const numPages = pdf.numPages;
        const textParts: string[] = [];

        // Extract text from each page
        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const textContent = await page.getTextContent();

          // Combine text items, preserving some structure
          let lastY: number | null = null;
          const pageText: string[] = [];

          for (const item of textContent.items) {
            if ('str' in item) {
              const textItem = item as { str: string; transform: number[] };
              const currentY = textItem.transform[5];

              // Add newline if we're on a new line (Y position changed significantly)
              if (lastY !== null && Math.abs(currentY - lastY) > 5) {
                pageText.push('\n');
              }

              pageText.push(textItem.str);
              lastY = currentY;
            }
          }

          textParts.push(pageText.join(' ').trim());
        }

        const fullText = textParts.join('\n\n').trim();

        // Check if we extracted any meaningful text
        if (!fullText || fullText.length < 10) {
          reject(new Error('PDF indeholder ingen læsbar tekst. Det kan være en scannet eller billed-baseret PDF.'));
          return;
        }

        resolve(fullText);
      } catch (error) {
        console.error('PDF extraction error:', error);
        reject(new Error('Kunne ikke læse PDF-filen. Den kan være beskadiget eller adgangskodebeskyttet.'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Kunne ikke indlæse filen. Prøv venligst igen.'));
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Read file content - supports both text files and PDFs
 * @param file The file to read
 * @returns Promise with the file content as text
 */
export const readFileContent = async (file: File): Promise<string> => {
  const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

  if (extension === '.pdf') {
    return extractTextFromPDF(file);
  }

  // Default to text file reading
  return readTextFile(file);
};